import React, { useState } from "react";
import { useBilling } from "../store/BillingContext";
import { useAuth } from "../store/AuthContext";
import type { CompanySettings } from "../types";

export const SettingsView: React.FC = () => {
  const { state, updateSettings, loadDemoData, resetData, addToast } = useBilling();
  const { user, logout, changePassword, resetToDefaultCredentials } = useAuth();
  const [form, setForm] = useState<CompanySettings>(() => structuredClone(state.settings));

  // Password management states
  const [currentPass, setCurrentPass] = useState("");
  const [newPass, setNewPass] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [passError, setPassError] = useState<string | null>(null);
  const [passSuccess, setPassSuccess] = useState<string | null>(null);
  const [isChangingPass, setIsChangingPass] = useState(false);

  const update = (field: keyof CompanySettings, val: any) => {
    setForm((prev) => ({ ...prev, [field]: val }));
  };

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    updateSettings(form);
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPassError(null);
    setPassSuccess(null);

    if (newPass !== confirmPass) {
      setPassError("New password and confirmation do not match.");
      return;
    }

    if (newPass.length < 6) {
      setPassError("New password must be at least 6 characters long.");
      return;
    }

    setIsChangingPass(true);
    try {
      const res = await changePassword(currentPass, newPass);
      if (res.success) {
        setPassSuccess("Password updated successfully!");
        setCurrentPass("");
        setNewPass("");
        setConfirmPass("");
        addToast("Security Updated", "Admin password changed successfully.", "success");
      } else {
        setPassError(res.error || "Failed to update password.");
      }
    } catch {
      setPassError("An error occurred while updating the password.");
    } finally {
      setIsChangingPass(false);
    }
  };

  const handleResetPassword = async () => {
    if (confirm("Reset admin password back to the default 'Revival@2026'?")) {
      await resetToDefaultCredentials();
      addToast("Password Reset", "Admin password reset to 'Revival@2026'.", "info");
      setPassSuccess("Password reset to default (Revival@2026).");
      setPassError(null);
    }
  };

  return (
    <section className="panel settings">
      <div className="panel-title">
        <div>
          <h3>Agency Profile & PDF Settings</h3>
          <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: "13px" }}>
            These details are printed on all generated tax invoices and quotation PDFs.
          </p>
        </div>
      </div>

      <form onSubmit={handleSave}>
        <div className="form-grid">
          <label>
            <span>Trading / Brand Name</span>
            <input
              type="text"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              required
            />
          </label>

          <label>
            <span>Legal Entity Name</span>
            <input
              type="text"
              value={form.legalName}
              onChange={(e) => update("legalName", e.target.value)}
              required
            />
          </label>

          <label>
            <span>Tagline</span>
            <input
              type="text"
              value={form.tagline}
              onChange={(e) => update("tagline", e.target.value)}
            />
          </label>

          <label>
            <span>Official Email</span>
            <input
              type="email"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              required
            />
          </label>

          <label>
            <span>Phone Numbers</span>
            <input
              type="text"
              value={form.phone}
              onChange={(e) => update("phone", e.target.value)}
              required
            />
          </label>

          <label>
            <span>Website URL</span>
            <input
              type="url"
              value={form.website}
              onChange={(e) => update("website", e.target.value)}
            />
          </label>

          <label>
            <span>Default GST Rate (%)</span>
            <input
              type="number"
              min="0"
              max="28"
              value={form.gst_rate}
              onChange={(e) => update("gst_rate", Number(e.target.value))}
              required
            />
          </label>

          <label>
            <span>GSTIN Number</span>
            <input
              type="text"
              value={form.gstin}
              onChange={(e) => update("gstin", e.target.value)}
              required
            />
          </label>

          <label>
            <span>Company PAN</span>
            <input
              type="text"
              value={form.pan}
              onChange={(e) => update("pan", e.target.value)}
              required
            />
          </label>

          <label>
            <span>Bank Name</span>
            <input
              type="text"
              value={form.bank}
              onChange={(e) => update("bank", e.target.value)}
              required
            />
          </label>

          <label>
            <span>Bank Account Number</span>
            <input
              type="text"
              value={form.acc}
              onChange={(e) => update("acc", e.target.value)}
              required
            />
          </label>

          <label>
            <span>IFSC Code</span>
            <input
              type="text"
              value={form.ifsc}
              onChange={(e) => update("ifsc", e.target.value)}
              required
            />
          </label>

          <label>
            <span>Account Type</span>
            <input
              type="text"
              value={form.acctype}
              onChange={(e) => update("acctype", e.target.value)}
              required
            />
          </label>

          <label className="wide">
            <span>Official Registered Address</span>
            <textarea
              value={form.address}
              rows={2}
              onChange={(e) => update("address", e.target.value)}
              required
            />
          </label>

          <label className="wide">
            <span>Default Invoice Terms & Notes</span>
            <textarea
              value={form.notes}
              rows={3}
              onChange={(e) => update("notes", e.target.value)}
            />
          </label>
        </div>

        <div style={{ marginTop: "24px", display: "flex", gap: "10px" }}>
          <button type="submit" className="primary">
            <i className="fa-solid fa-floppy-disk" /> Save Settings
          </button>
        </div>
      </form>

      {/* Security & Authentication Section */}
      <div style={{ marginTop: "40px", borderTop: "1px solid #e2e8f0", paddingTop: "24px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "12px", marginBottom: "16px" }}>
          <div>
            <h4 style={{ margin: "0 0 4px", color: "#0d263e", fontSize: "16px", fontWeight: 700, display: "flex", alignItems: "center", gap: "8px" }}>
              <i className="fa-solid fa-shield-halved" style={{ color: "#d4ac42" }} />
              Admin Authentication & Security
            </h4>
            <p style={{ margin: 0, color: "#64748b", fontSize: "13px" }}>
              Manage password credentials and active session for Revival Softech Billing Hub.
            </p>
          </div>
          <button
            type="button"
            className="danger"
            onClick={logout}
            style={{ padding: "7px 14px", fontSize: "13px" }}
          >
            <i className="fa-solid fa-arrow-right-from-bracket" /> Sign Out of Hub
          </button>
        </div>

        {/* Current Session Info Card */}
        <div className="security-info-card">
          <div className="security-badge">
            <span className="dot" style={{ background: "#10b981" }} />
            <span>Active Session: <b>{user?.email || "revivalsoftech@gmail.com"}</b></span>
          </div>
          <div className="security-tags">
            <span className="sec-tag"><i className="fa-solid fa-lock" /> 256-Bit Encrypted</span>
            <span className="sec-tag"><i className="fa-solid fa-user-shield" /> Super Admin</span>
          </div>
        </div>

        {/* Password Update Form */}
        <div className="security-password-box">
          <h5 style={{ margin: "0 0 12px", fontSize: "14px", fontWeight: 600, color: "#0d263e" }}>
            Change Account Password
          </h5>

          {passSuccess && (
            <div style={{ marginBottom: "12px", padding: "10px 14px", background: "#ecfdf5", border: "1px solid #a7f3d0", borderRadius: "8px", color: "#065f46", fontSize: "13px", display: "flex", alignItems: "center", gap: "8px" }}>
              <i className="fa-solid fa-circle-check" />
              <span>{passSuccess}</span>
            </div>
          )}

          {passError && (
            <div style={{ marginBottom: "12px", padding: "10px 14px", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "8px", color: "#991b1b", fontSize: "13px", display: "flex", alignItems: "center", gap: "8px" }}>
              <i className="fa-solid fa-circle-exclamation" />
              <span>{passError}</span>
            </div>
          )}

          <form onSubmit={handlePasswordChange}>
            <div className="form-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
              <label>
                <span>Current Password</span>
                <input
                  type="password"
                  value={currentPass}
                  onChange={(e) => setCurrentPass(e.target.value)}
                  placeholder="Enter current password"
                  required
                />
              </label>

              <label>
                <span>New Password</span>
                <input
                  type="password"
                  value={newPass}
                  onChange={(e) => setNewPass(e.target.value)}
                  placeholder="At least 6 characters"
                  required
                />
              </label>

              <label>
                <span>Confirm New Password</span>
                <input
                  type="password"
                  value={confirmPass}
                  onChange={(e) => setConfirmPass(e.target.value)}
                  placeholder="Repeat new password"
                  required
                />
              </label>
            </div>

            <div style={{ marginTop: "16px", display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
              <button type="submit" className="primary" disabled={isChangingPass}>
                {isChangingPass ? (
                  <>
                    <i className="fa-solid fa-circle-notch fa-spin" /> Updating...
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-key" /> Update Admin Password
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={handleResetPassword}
                title="Reset password back to Revival@2026 default"
                style={{ fontSize: "12px", background: "#f1f5f9", borderColor: "#cbd5e1" }}
              >
                <i className="fa-solid fa-rotate-left" /> Reset to Default Password
              </button>
            </div>
          </form>
        </div>
      </div>

      <div style={{ marginTop: "40px", borderTop: "1px solid #e2e8f0", paddingTop: "20px" }}>
        <h4 style={{ margin: "0 0 10px", color: "#64748b", textTransform: "uppercase", fontSize: "12px" }}>
          Data Management
        </h4>
        <div className="settings-actions">
          <button
            type="button"
            onClick={() => {
              if (confirm("Reset and reload the authentic Revival Softech dataset?")) {
                loadDemoData();
              }
            }}
          >
            <i className="fa-solid fa-arrows-rotate" /> Reload Authentic Dataset
          </button>
          <button
            type="button"
            className="danger"
            onClick={() => {
              if (confirm("Warning: This will clear all local invoices and documents. Proceed?")) {
                resetData();
              }
            }}
          >
            <i className="fa-solid fa-trash" /> Start with Empty Workspace
          </button>
        </div>
      </div>
    </section>
  );
};
